'use client';

import { adyenPay, applyAppleSession, getApplePaymentMethods } from '@/api/pay';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { accMul } from '@/utils/tools'; // 你需要确保这个工具函数存在
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { useMount } from 'ahooks';
import { OrderIdNo } from '../type';

interface ApplePayProps {
  from?: 'payment' | 'order' | 'vip';
  createOrder?: (type: string) => Promise<{ order_id: string; order_no: string }>;
  grandTotal?: number | string;
  order_id?: string;
  order_no?: string;
  shipping: { country_id: string };
  order?: any;
  isUseWallet?: number;
  payAmountWithWallet?: string;
  onPaySuccess?: (data?: any) => void;
  onPayFail?: (data?: OrderIdNo) => void;
  onValid?: (valid: boolean) => void;
  ref?: React.RefObject<any>;
}

const ApplePay: React.FC<ApplePayProps> = ({
  from = '',
  createOrder,
  grandTotal = 0,
  order_id,
  order_no,
  shipping,
  order,
  isUseWallet = 2,
  payAmountWithWallet = '0.00',
  onPaySuccess,
  onPayFail,
  onValid,
  ref,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  const applepayComponent = useRef<any>(null);
  const currency = useStore((state) => state.currency);
  const PaymentType = 'applepay';

  const orderIdNo = useRef<OrderIdNo>({ order_id: '', order_no: '' });

  useImperativeHandle(ref, () => ({
    payAction,
  }));

  useMount(() => {
    logger.debug('[ApplePay] init firstly');
    payAction(false);
  });

  const getPaymentMethod = async () => {
    try {
      const { data } = await getApplePaymentMethods({
        country_id: shipping.country_id || env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
        currency: from === 'order' ? order?.purchase_currency : currency.name,
        pay_type: 'applepay',
      });
      setPaymentMethod(data);
      return data;
    } catch (err) {
      logger.error(err);
      return null;
    }
  };

  const payAction = async (isTrigger: boolean) => {
    logger.debug('[ApplePay] paymentMethod', paymentMethod);
    if (!paymentMethod) await getPaymentMethod();
    // if (!paymentMethod) return;

    /* 直接调起时，需要先创建订单 */
    if (isTrigger) {
      if (from === 'order') {
        orderIdNo.current = { order_id: order_id || '', order_no: order_no || '' };
      } else {
        // 支付页，等到获取到session之后再创建订单，避免触发错误。Must create a new ApplePaySession from a user gesture handler.
      }
    }

    const configuration = {
      showPayButton: true,
      paymentMethodsResponse: paymentMethod,
      clientKey: env.NEXT_PUBLIC_AYDEN_CLIENT_KEY,
      locale: env.NEXT_PUBLIC_FULL_LOCALE.replace('_', '-'), // 苹果支付的locale需要是-分隔的，比如en_US需要是en-US
      environment: env.NEXT_PUBLIC_AYDEN_ENVIROMENT,
      onSubmit: async (state: any) => {
        try {
          const res = await adyenPay({
            order_id: orderIdNo.current.order_id,
            payment_method: JSON.stringify(state.data.paymentMethod),
            payment_type: PaymentType,
            is_bind: 2,
          });

          if (res && res.code === 200) {
            onPaySuccess?.(orderIdNo.current);
          } else {
            onPayFail?.(orderIdNo.current);
          }
        } catch {
          onPayFail?.(orderIdNo.current);
        }
      },
      onChange: () => {},
      onError: (error: any, component: any) => {
        logger.error('applepay error', error.name, error.message, error.stack, component);
        onPayFail?.(orderIdNo.current);
      },
      onCancel: () => {
        onPayFail?.(orderIdNo.current);
      },
    };
    logger.debug('[ApplePay] isUseWallet', isUseWallet);
    const applePayAmount =
      isUseWallet === 1 && parseFloat(payAmountWithWallet) > 0 ? payAmountWithWallet : grandTotal;
    logger.debug('[ApplePay] applePayAmount', applePayAmount);

    let merchantId = '';
    let merchantName = '';

    if (paymentMethod?.paymentMethods?.length > 0) {
      merchantId = paymentMethod.paymentMethods[0].configuration.merchantId;
      merchantName = paymentMethod.paymentMethods[0].configuration.merchantName;
    }

    const applePayConfiguration = {
      environment: process.env.NEXT_PUBLIC_ENV === 'production' ? 'PRODUCTION' : 'TEST',
      total: {
        label: 'Zeelool Inc.',
        type: 'final',
        amount: accMul(applePayAmount, 100),
      },
      merchantAccount: process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID,
      amount: {
        value: accMul(applePayAmount, 100),
        currency: from === 'order' ? order?.purchase_currency : currency.name,
      },
      countryCode: shipping.country_id,
      currencyCode: from === 'order' ? order?.purchase_currency : currency.name,
      configuration: {
        merchantName: 'Zeelool Inc.',
        merchantIdentifier:
          process.env.NEXT_PUBLIC_ENV === 'production'
            ? 'merchant.com.adyen.zeelool.usa'
            : 'merchant.com.adyen.zeelool.usa.test',
      },
      onValidateMerchant: (resolve: any, reject: any, validationURL: string) => {
        return applyAppleSession(
          // fixme debug 支付问题
          ['uk', 'ca', 'fr', 'de'].includes(env.NEXT_PUBLIC_SITE_NAME)
            ? {
                display_name: merchantName,
                domain_name: window.location.hostname,
                merchant_identifier: merchantId,
              }
            : { url: validationURL },
        )
          .then(async (res: any) => {
            if (from !== 'order' && createOrder) {
              const data = await createOrder(PaymentType);
              orderIdNo.current = { order_id: data.order_id, order_no: data.order_no };
            }
            resolve(res.data);
          })
          .catch(() => {
            reject();
          });
      },
    };

    const checkout = new AdyenCheckout(configuration);
    logger.debug('[ApplePay] applePayConfiguration', applePayConfiguration);
    checkout.then((data: any) => {
      applepayComponent.current = data.create('applepay', applePayConfiguration);
      applepayComponent.current
        .isAvailable()
        .then((cb: boolean) => {
          // eslint-disable-next-line no-constant-condition
          if (cb || true) {
            // 暂时不判断cb值
            /* 因为苹果支付弹框必须显示总价，所以在点击支付之前不实际挂载，仅仅测试当前设备是否支持Apple Pay */
            if (isTrigger) {
              setTimeout(() => {
                applepayComponent.current.submit();
              }, 0);
            } else {
              logger.debug('[ApplePay] 苹果支付验证成功');
              onValid?.(true);
            }
          }
        })
        .catch(() => {
          logger.debug('[ApplePay] 苹果支付验证失败');
          onValid?.(false);
        });
    });
  };

  return (
    <div className="xms-applepay">
      <div />
    </div>
  );
};

export default ApplePay;
